import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  loansApi,
  PAY_HEAD_OPTIONS,
  LOAN_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from '../api/loansApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'company_name', label: 'Company' },
  { key: 'branch_name', label: 'Branch' },
  { key: 'department_name', label: 'Department' },
  { key: 'emp_id', label: 'Emp ID' },
  { key: 'emp_name', label: 'Employee Name' },
  { key: 'loan_date_formatted', label: 'Loan Date' },
  { key: 'loan_amt', label: 'Loan Amount', align: 'right', format: 'currency' },
  { key: 'emi_amt', label: 'EMI Amount', align: 'right', format: 'currency' },
  { key: 'emi_from_date_formatted', label: 'EMI From' },
  { key: 'reason_name', label: 'Loan Reason' },
  { key: 'status', label: 'Status' },
];

export function useLoansList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Search & Filter state
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get('status') || 'Pending'
  );
  const [companyFilter, setCompanyFilter] = useState('All');
  const [branchFilter, setBranchFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Data state
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Background Export integration
  const { triggerExport, isExporting } = useExportJob({
    module: 'emp_loans',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  // Fetch loans matching filters
  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await loansApi.getLoans({
        status: statusFilter,
        name: searchText,
        company: companyFilter,
        branch: branchFilter,
        department: departmentFilter,
        fromDate,
        toDate,
      });
      setRecords(data);
    } catch (err) {
      console.error('Failed to load loans records', err);
      appToast.error('Failed to load loan application records');
    } finally {
      setIsLoading(false);
    }
  }, [
    statusFilter,
    searchText,
    companyFilter,
    branchFilter,
    departmentFilter,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const handleResetFilters = () => {
    setSearchField('Name');
    setSearchText('');
    setStatusFilter('Pending');
    setCompanyFilter('All');
    setBranchFilter('All');
    setDepartmentFilter('All');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const handleStatusTabChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handleApprove = async (id, empName) => {
    const confirmed = await appConfirm({
      title: 'Approve Loan Application',
      message: `Are you sure you want to approve this loan application for ${empName || 'this employee'}?`,
      confirmText: 'Approve',
      cancelText: 'Cancel',
      variant: 'primary',
    });

    if (!confirmed) return;

    try {
      await loansApi.updateLoanStatus(id, 'Approved');
      appToast.success('Loan application approved successfully!');
      fetchRecords();
    } catch (err) {
      console.error('Error approving loan:', err);
      appToast.error('Failed to approve loan application.');
    }
  };

  const handleReject = async (id, empName) => {
    const confirmed = await appConfirm({
      title: 'Reject Loan Application',
      message: `Are you sure you want to reject this loan application for ${empName || 'this employee'}?`,
      confirmText: 'Reject',
      cancelText: 'Cancel',
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      await loansApi.updateLoanStatus(id, 'Rejected');
      appToast.success('Loan application marked as rejected.');
      fetchRecords();
    } catch (err) {
      console.error('Error rejecting loan:', err);
      appToast.error('Failed to reject loan application.');
    }
  };

  const handleDelete = async (id, empName) => {
    const confirmed = await appConfirm({
      title: 'Delete Loan Application',
      message: `Are you sure you want to delete the loan application for ${empName || 'this employee'}? This will soft delete the record.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await loansApi.deleteLoan(id);
      appToast.success('Loan application deleted successfully.');
      fetchRecords();
    } catch (err) {
      console.error('Error deleting loan:', err);
      appToast.error('Failed to delete loan application.');
    }
  };

  const handleToolSelect = (toolKey) => {
    const activeFilters = {
      status: statusFilter !== 'All' ? statusFilter : undefined,
      search: searchText.trim() || undefined,
    };

    if (toolKey === 'print') {
      executePrintReport({
        title: 'Employee Loans & Advance Report',
        columns: REPORT_COLUMNS,
        metadata: {
          'Status': statusFilter || 'All',
          'Search': searchText || 'All',
        },
        fetchReportData: async () => {
          const res = await loansApi.getLoansReport(activeFilters);
          return { items: res?.items || [], total_amount: res?.total_amount || 0 };
        },
        onExportPdf: () => triggerExport(activeFilters, 'PDF'),
        onExportExcel: () => triggerExport(activeFilters, 'XLSX'),
        threshold: 1000,
      });
    } else if (toolKey === 'export_excel') {
      triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      triggerExport(activeFilters, 'PDF');
    }
  };

  const totalCount = records.length;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const paginatedRecords = records.slice((page - 1) * pageSize, page * pageSize);

  const searchFieldOptions = [
    { value: 'Name', label: 'Employee Name' },
  ];

  const tools = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export Excel', icon: 'description' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
  ];

  return {
    state: {
      searchField,
      searchText,
      statusFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
      fromDate,
      toDate,
      page,
      pageSize,
      totalCount,
      totalPages,
      records: paginatedRecords,
      isLoading,
      isExporting,
      searchFieldOptions,
      statusOptions: STATUS_OPTIONS,
      tools,
    },
    actions: {
      setSearchField,
      setSearchText,
      setStatusFilter,
      setCompanyFilter,
      setBranchFilter,
      setDepartmentFilter,
      setFromDate,
      setToDate,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters: handleResetFilters,
      handleStatusTabChange,
      handleApprove,
      handleReject,
      handleDelete,
      handleToolSelect,
      reload: fetchRecords,
    },
  };
}
